import React, { memo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import useKeyDownListener from 'hooks/useKeyDownListener';
import SuppliersSvg from 'images/v5/icons/menu/suppliers.svg';
import Input from 'layout/atoms/Input/Input';
import Label from 'layout/atoms/Label/Label';
import Modal from 'layout/atoms/Modal/Modal';
import FormField from 'layout/molecules/FormField/FormField';
import FormSaveActions from 'layout/molecules/FormSaveActions/FormSaveActions';
import Grid from 'layout/molecules/Grid/Grid';
import { closeModal } from 'store/layoutSlice';
import { addSupplier } from 'store/suppliersSlice';
import useSupplierValidation from '../useSupplierValidation';
function SupplierCreator() {
    const { t } = useTranslation(['specification', 'common']);
    const dispatch = useDispatch();
    const tenantId = useSelector((state) => { var _a; return (_a = state.user.tennant) === null || _a === void 0 ? void 0 : _a.id; });
    const isLoading = useSelector((state) => state.suppliers.status.add === 'loading');
    const [supplierForm, setSupplierForm] = useState({
        name: '',
        shortName: '',
        code: '',
        vatNumber: '',
        countryOfOrigin: '',
        addressPostalCode: '',
        addressCity: '',
        addressStreet: '',
        addressNumber: '',
        emailAddress: '',
        contactPhone: '',
    });
    const { errors, isValid, pendingFields, markTouched, validateAll } = useSupplierValidation(supplierForm, t);
    const handleCloseModal = () => {
        dispatch(closeModal({ name: 'supplierCreator' }));
    };
    const handleVariantChange = ({ target: { name, value }, }) => {
        setSupplierForm((spplr) => (Object.assign(Object.assign({}, spplr), { [name]: value })));
    };
    const handleFormSubmit = () => {
        if (!validateAll())
            return;
        dispatch(addSupplier(Object.assign({ tenantId: tenantId !== null && tenantId !== void 0 ? tenantId : '' }, supplierForm)))
            .unwrap()
            .then(() => {
            dispatch(closeModal({ name: 'supplierCreator' }));
        });
    };
    useKeyDownListener([
        { code: 'Enter', callback: handleFormSubmit },
        { code: 'Escape', callback: handleCloseModal },
    ]);
    return (React.createElement(Modal, { name: "supplierCreator", size: "m", header: React.createElement(Label, { weight: "strong" },
            React.createElement(SuppliersSvg, null),
            t('specification:add_supplier')), footer: React.createElement(FormSaveActions, { isValid: isValid, isLoading: isLoading, pendingFields: pendingFields, onSave: handleFormSubmit, onCancel: handleCloseModal, validateAll: validateAll }) },
        React.createElement(Grid, { gap: 32 },
            React.createElement(Grid, null,
                React.createElement(Label, null, t('common:basic_info')),
                React.createElement(FormField, { direction: "row", label: t('supplier_name_capitalize'), error: errors.name },
                    React.createElement(Input, { value: supplierForm.name, onChange: handleVariantChange, onBlur: () => markTouched('name'), name: "name", error: Boolean(errors.name) })),
                React.createElement(FormField, { direction: "row", label: t('supplier_shortname'), error: errors.shortName },
                    React.createElement(Input, { value: supplierForm.shortName, onChange: handleVariantChange, onBlur: () => markTouched('shortName'), name: "shortName", error: Boolean(errors.shortName) })),
                React.createElement(FormField, { direction: "row", label: t('supplier_code'), error: errors.code },
                    React.createElement(Input, { value: supplierForm.code, onChange: handleVariantChange, onBlur: () => markTouched('code'), name: "code", error: Boolean(errors.code) })),
                React.createElement(FormField, { direction: "row", label: t('specification:vat_number') },
                    React.createElement(Input, { value: supplierForm.vatNumber, onChange: handleVariantChange, name: "vatNumber" }))),
            React.createElement(Grid, null,
                React.createElement(Label, null, t('common:address')),
                React.createElement(FormField, { direction: "row", label: t('specification:country_of_origin') },
                    React.createElement(Input, { value: supplierForm.countryOfOrigin, onChange: handleVariantChange, name: "countryOfOrigin" })),
                React.createElement(FormField, { direction: "row", label: t('specification:address_postal_code') },
                    React.createElement(Input, { value: supplierForm.addressPostalCode, onChange: handleVariantChange, name: "addressPostalCode" })),
                React.createElement(FormField, { direction: "row", label: t('specification:address_city') },
                    React.createElement(Input, { value: supplierForm.addressCity, onChange: handleVariantChange, name: "addressCity" })),
                React.createElement(FormField, { direction: "row", label: t('specification:address_street') },
                    React.createElement(Input, { value: supplierForm.addressStreet, onChange: handleVariantChange, name: "addressStreet" })),
                React.createElement(FormField, { direction: "row", label: t('specification:address_number') },
                    React.createElement(Input, { value: supplierForm.addressNumber, onChange: handleVariantChange, name: "addressNumber" })),
                React.createElement(FormField, { direction: "row", label: t('specification:email_address') },
                    React.createElement(Input, { value: supplierForm.emailAddress, onChange: handleVariantChange, name: "emailAddress" })),
                React.createElement(FormField, { direction: "row", label: t('specification:contact_phone') },
                    React.createElement(Input, { value: supplierForm.contactPhone, onChange: handleVariantChange, name: "contactPhone" }))))));
}
export default memo(SupplierCreator);
